import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, InputGroup, InputGroupAddon, Row } from 'shards-react';
import SelectOrganization from '../../../components/common/SelectOrganization';
import SelectOptionsType from '../../common/SelectOptionsType';
import useLocales from '../../../hooks/useLocales';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { selectSteelMaker, selectBusinessType, selectSteelCategory, confirmCondition, getCondition, resetCondition } from '../../../redux/slices/condition';
import type { OptionsType } from '../../../types/options';
import type { OrganizationType } from '../../../types/organization';
import type { BusinessType, SteelCategory, SteelMaker } from '../../../types/common';
import type { CreateWhenNotExistParams } from '../../../types/condition';

const SelectCondition = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { businessType, organizationBase, steelCategory, steelMaker } = useSelector((state: RootState) => state.options);
  const { condition, conditionId } = useSelector((state: RootState) => state.condition);
  // 選択された組織
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationType | null>(null);
  // 区分、鉄鋼M、分類のプルダウンオプション
  const [steelMakerOptions, setSteelMakerOptions] = useState<OptionsType<SteelMaker>[]>([]);
  const [selectedSteelMaker, setSelectedSteelMaker] = useState<OptionsType<SteelMaker> | null>(null);
  const [businessTypeOptions, setBusinessTypeOptions] = useState<OptionsType<BusinessType>[]>([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState<OptionsType<BusinessType> | null>(null);
  const [steelCategoryOptions, setSteelCategoryOptions] = useState<OptionsType<SteelCategory>[]>([]);
  const [selectedSteelCategory, setSelectedSteelCategory] = useState<OptionsType<SteelCategory> | null>(null);
  // 組織選択プルダウンのフィルタ関数
  const optionsFilter = (options: OrganizationType[] | undefined): OrganizationType[] => {
    const newOrganizations: OrganizationType[] = options ? options.filter((option) => !option.isTradingCompany) : [];
    return newOrganizations;
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * organizationが変更されたら鉄鋼M・区分・分類の選択状態をリセットし、プルダウン選択肢を更新する
   */
  useEffect(() => {
    // console.log(selectedOrganization);
    // プルダウン選択状態のリセット
    // ========================================
    setSelectedSteelMaker(null);
    setSelectedBusinessType(null);
    setSelectedSteelCategory(null);

    // 組織に対応する区分プルダウン選択肢の更新
    // ========================================
    // 選択された組織の区分からOptionsTypeに変換する。
    // 該当する区分のOptionsTypeが見つからない場合は選択肢として表示しない。
    const newBusinessTypeOptions: OptionsType<BusinessType>[] | undefined = selectedOrganization?.businessType.map((type) => businessType.find((option) => option.code === type));
    // console.log(newBusinessTypeOptions);
    setBusinessTypeOptions(newBusinessTypeOptions ?? []);

    // 組織に対応する鉄鋼Mプルダウン選択肢の更新
    // ========================================
    // 選択された組織の鉄鋼MからOptionsTypeに変換する。
    // 該当する鉄鋼MのOptionsTypeが見つからない場合は選択肢として表示しない。
    const newSteelMakerOptions: OptionsType<SteelMaker>[] | undefined = selectedOrganization?.steelMaker.map((maker) => steelMaker.find((option) => option.code === maker));
    // console.log(newSteelMakerOptions);
    setSteelMakerOptions(newSteelMakerOptions ?? []);

    // 組織に対応する分類プルダウン選択肢の更新
    // ========================================
    // 選択された組織の分類からOptionsTypeに変換する。
    // 該当する分類のOptionsTypeが見つからない場合は選択肢として表示しない。
    const newSteelCategoryOptions: OptionsType<SteelCategory>[] | undefined = selectedOrganization?.steelCategory.map((category) => steelCategory.find((option) => option.code === category));
    // console.log(newSteelCategoryOptions);
    setSteelCategoryOptions(newSteelCategoryOptions ?? []);
  }, [selectedOrganization]);

  /**
   * steelMaker, businessType, steelCategoryが変わったらconditionを再取得する
   */
  useEffect(() => {
    if (selectedSteelMaker !== null && selectedBusinessType !== null && selectedSteelCategory !== null) {
      const params: CreateWhenNotExistParams = {
        organizationId: selectedOrganization.id,
        steelMaker: selectedSteelMaker.code,
        businessType: selectedBusinessType.code,
        steelCategory: selectedSteelCategory.code,
      };
      dispatch(confirmCondition(params));
    } else {
      // do nothing
    }
  }, [selectedSteelMaker, selectedBusinessType, selectedSteelCategory]);

  /**
   * dispatch confirmConditionで取得したconditionIdで可視制御を検索する
   */
  useEffect(() => {
    if (conditionId !== undefined) {
      dispatch(getCondition(conditionId));
    }
  }, [conditionId]);

  /**
   * ステータスが変わったらconditionを再取得する
   */
  useEffect(() => {
    if (condition) {
      dispatch(getCondition(condition.id));
    } else {
      // do nothing
    }
  }, [condition?.conditionStatus]);

  // EVENT HANDLER
  // ================================================================================

  const handleOrganizationChange = (org: OrganizationType | null): void => {
    // console.log(org);
    setSelectedOrganization(org);
    dispatch(resetCondition());
  };

  const handleSteelMakerChange = (steelMaker: OptionsType<SteelMaker>): void => {
    setSelectedSteelMaker(steelMaker);
    dispatch(selectSteelMaker(steelMaker.code));
  };
  const handleBusinessTypeChange = (businessType: OptionsType<BusinessType>): void => {
    setSelectedBusinessType(businessType);
    dispatch(selectBusinessType(businessType.code));
  };
  const handleSteelCategoryChange = (steelCategory: OptionsType<SteelCategory>): void => {
    setSelectedSteelCategory(steelCategory);
    dispatch(selectSteelCategory(steelCategory.code));
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <Row className="mb-2">
        <Col sm="12" md="6" className="mb-sm-4 mb-md-0">
          <SelectOrganization selected={selectedOrganization} onChange={handleOrganizationChange} optionsFilter={optionsFilter} />
        </Col>
      </Row>

      <Row>
        <Col className="mb-sm-4 mb-md-0">
          <InputGroup>
            <InputGroupAddon type="prepend" style={{ width: '33%' }}>
              <SelectOptionsType
                id="selectSteelMaker"
                className="w-100"
                value={selectedSteelMaker}
                options={steelMakerOptions}
                placeholder={t('steelMaker')}
                isDisabled={organizationBase === null}
                language={currentLang.value}
                onChange={handleSteelMakerChange}
              />
            </InputGroupAddon>
            <InputGroupAddon type="append" style={{ width: '34%' }}>
              <SelectOptionsType
                id="selectBusinessType"
                className="w-100"
                value={selectedBusinessType}
                options={businessTypeOptions}
                placeholder={t('businessType')}
                isDisabled={organizationBase === null}
                language={currentLang.value}
                onChange={handleBusinessTypeChange}
              />
            </InputGroupAddon>
            <InputGroupAddon type="append" style={{ width: '33%' }}>
              <SelectOptionsType
                id="selectSteelCategory"
                className="w-100"
                value={selectedSteelCategory}
                options={steelCategoryOptions}
                placeholder={t('steelCategory')}
                isDisabled={organizationBase === null}
                language={currentLang.value}
                onChange={handleSteelCategoryChange}
              />
            </InputGroupAddon>
          </InputGroup>
        </Col>
      </Row>
    </>
  );
};

export default SelectCondition;
